import { Button, Typography } from "@hellocarbo/lasagne";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ActionFunctionArgs,
  Form,
  redirect,
  useLoaderData,
  useNavigation,
} from "react-router";
import invariant from "tiny-invariant";

import { SimulationScenarioItem } from "@/components/Simulation";
import UpsellModal from "@/components/Simulation/UpsellModal";
import Card from "@/components/ui/Card";
import { TScenario, TSimulation } from "@/types";
import { ScenarioIcons } from "@/utils/scenario";

export async function loader() {
  const response = await fetch("/api/client/simulations/scenarios");

  if (response.status !== 200) {
    return { ok: false };
  }

  const { data: scenarios } = (await response.json()) as {
    data: Array<TScenario>;
  };

  return { ok: true, scenarios };
}

export async function action({ request }: ActionFunctionArgs) {
  const formData = await request.formData();
  const scenario = formData.get("scenario");

  const response = await fetch("/api/client/simulations", {
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      scenario: scenario !== TScenario.free ? scenario : null,
    }),
    method: "post",
  });

  const { data: simulation } = (await response.json()) as { data: TSimulation };

  return redirect(`/simulations/${simulation.id}/parameters`);
}

function SimulationScenarios() {
  const { scenarios } = useLoaderData<typeof loader>();
  const [currentScenario, setCurrentScenario] = useState<TScenario | null>(
    null
  );
  const { t } = useTranslation("simulation");
  const navigation = useNavigation();

  invariant(scenarios, "No scenarios found!");

  return (
    <div className="mx-xl my-lg">
      <Typography step="3" as="h1" weight="bold" className="mb-sm">
        {t("simulation:createSimulation")}
      </Typography>
      <Card>
        <Typography as="h2" step="2" weight="bold" className="mb-xs">
          {t("simulation:selectScenario.title")}
        </Typography>
        <Typography>{t("simulation:selectScenario.description")}</Typography>
        <Form className="text-center" method="post">
          <ul className="my-lg gap-md grid grid-cols-3">
            {scenarios.map((scenario) => (
              <SimulationScenarioItem
                key={scenario}
                value={scenario}
                Icon={ScenarioIcons[scenario]}
                onChange={() => setCurrentScenario(scenario)}
              />
            ))}
            <SimulationScenarioItem
              value={TScenario.free}
              Icon={ScenarioIcons[TScenario.free]}
              onChange={() => setCurrentScenario(TScenario.free)}
            />
          </ul>
          <UpsellModal isDisabled={!currentScenario}>
            <Button
              disabled={!currentScenario}
              type="submit"
              isLoading={Boolean(
                currentScenario && navigation.state !== "idle"
              )}
            >
              {t("simulation:create")}
            </Button>
          </UpsellModal>
        </Form>
      </Card>
    </div>
  );
}

export default SimulationScenarios;
